import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, Link } from 'react-router-dom';
import { fetchMe } from '../slices/authSlice';
import api from '../api';
import {
  HiOutlineExternalLink, HiOutlineX, HiOutlineShieldCheck,
  HiOutlineKey, HiOutlineCalendar, HiOutlineMail, HiOutlineStar,
  HiOutlineUser, HiOutlineLightningBolt, HiOutlineGlobe,
  HiOutlineCheckCircle, HiOutlineExclamationCircle,
} from 'react-icons/hi';

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        {Icon && <Icon size={15} />} {label}
      </div>
      <span className="text-sm font-medium text-right">{value ?? '—'}</span>
    </div>
  );
}


export default function Settings() {
  const user = useSelector((s) => s.auth.user);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connecting, setConnecting] = useState(false);
  const [connectingFb, setConnectingFb] = useState(false);
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [facebookStatusLoading, setFacebookStatusLoading] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Handle OAuth redirect callbacks
  useEffect(() => {
    const google = searchParams.get('google');
    const facebook = searchParams.get('facebook');

    if (google === 'connected') {
      showToast('success', 'Google Sheets connected successfully!');
      dispatch(fetchMe());
      setSearchParams({});
    } else if (google === 'error') {
      showToast('error', 'Failed to connect Google. Please try again.');
      setSearchParams({});
    }

    if (facebook === 'connected') {
      showToast('success', 'Facebook connected successfully!');
      dispatch(fetchMe());
      setSearchParams({});
    } else if (facebook === 'error') {
      showToast('error', 'Failed to connect Facebook. Please try again.');
      setSearchParams({});
    }
  }, [dispatch, searchParams, setSearchParams]);

  useEffect(() => {
    let active = true;

    const loadFacebookStatus = async () => {
      if (!user?.facebookConnected) {
        if (active) setFacebookStatus(null);
        return;
      }

      try {
        setFacebookStatusLoading(true);
        const res = await api.get('/facebook/status');
        if (active) setFacebookStatus(res.data);
      } catch (_err) {
        if (active) setFacebookStatus(null);
      } finally {
        if (active) setFacebookStatusLoading(false);
      }
    };

    loadFacebookStatus();
    return () => {
      active = false;
    };
  }, [user?.facebookConnected]);

  const handleConnectGoogle = async () => {
    try {
      setConnecting(true);
      const res = await api.get('/google/auth-url');
      const url = new URL(res.data.url);
      url.searchParams.set('state', user.id);
      window.location.href = url.toString();
    } catch (err) {
      showToast('error', 'Failed to start Google connection');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google account?')) return;
    try {
      await api.delete('/google/disconnect');
      dispatch(fetchMe());
      showToast('success', 'Google disconnected');
    } catch (err) {
      showToast('error', 'Failed to disconnect Google');
    }
  };

  const handleConnectFacebook = async () => {
    try {
      setConnectingFb(true);
      const res = await api.get('/facebook/auth-url');
      window.location.href = res.data.url;
    } catch (err) {
      showToast('error', 'Failed to start Facebook connection');
      setConnectingFb(false);
    }
  };

  const handleDisconnectFacebook = async () => {
    if (!window.confirm('Disconnect Facebook account?')) return;
    try {
      await api.delete('/facebook/disconnect');
      dispatch(fetchMe());
      showToast('success', 'Facebook disconnected');
    } catch (err) {
      showToast('error', 'Failed to disconnect Facebook');
    }
  };

  const facebookExpiresAt = facebookStatus?.expiresAt || user?.facebookExpiresAt;

  return (
    <div className="animate-fade-in max-w-3xl space-y-6">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-fade-in ${
            toast.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/40 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700'
          }`}
        >
          {toast.type === 'success'
            ? <HiOutlineCheckCircle size={18} />
            : <HiOutlineExclamationCircle size={18} />}
          {toast.message}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Account settings and integrations</p>
      </div>

      {/* Account Info */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="font-semibold mb-4">Account</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
            <span className="text-sm font-medium">{user?.email}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Plan</span>
            <span className="text-sm font-semibold uppercase text-brand-600">{user?.plan}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Credits</span>
            <span className="text-sm font-medium">{user?.credits?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Next Credit Reset</span>
            <span className="text-sm font-medium">
              {user?.creditsResetDate ? new Date(user.creditsResetDate).toLocaleDateString() : '—'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Member Since</span>
            <span className="text-sm font-medium">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Facebook Integration */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-sm">Facebook Lead Ads</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Connect to select pages, subscribe to lead forms, and trigger automated workflows.
            </p>
          </div>
        </div>

        {user?.facebookConnected ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-[#1877F2]/5 dark:bg-[#1877F2]/10 rounded-lg border border-[#1877F2]/20">
              <div className="flex items-center gap-2 text-[#1877F2] text-sm font-medium">
                <HiOutlineCheckCircle size={18} />
                Facebook account connected
              </div>
              <button
                onClick={handleDisconnectFacebook}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
              >
                Disconnect
              </button>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950/50">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Accessible Pages</p>
                <p className="text-lg font-semibold">{facebookStatusLoading ? 'Loading...' : facebookStatus?.pageCount ?? '—'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">All pages returned by Meta for this login.</p>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950/50">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Token Valid Until</p>
                <p className="text-sm font-semibold">{facebookExpiresAt ? new Date(facebookExpiresAt).toLocaleString() : 'Unknown'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">The backend refreshes long-lived tokens before expiry.</p>
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-950/50">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Connection Health</p>
                <p className="text-sm font-semibold">
                  {facebookStatus?.expiresSoon ? 'Reconnect soon' : 'Healthy'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">If page count is wrong, reconnect with the same Facebook user that manages both pages.</p>
              </div>
            </div>

            {facebookStatus?.pages?.length > 0 && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/50">
                  <h4 className="text-sm font-semibold">Pages Returned By Facebook</h4>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {facebookStatus.pages.map((page) => (
                    <div key={page.id} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{page.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{page.category || 'Facebook Page'}</p>
                      </div>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-mono">
                        {page.tasks?.join(', ') || 'no tasks'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Connect your Facebook account to enable the <strong>Facebook Lead Trigger</strong> node in your scenarios. You'll be able to select pages, choose lead forms, and map fields.
            </p>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1.5">
                {['pages_show_list', 'leads_retrieval', 'pages_read_engagement', 'pages_manage_ads'].map((p) => (
                  <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-mono">
                    {p}
                  </span>
                ))}
              </div>
              <button
                onClick={handleConnectFacebook}
                disabled={connectingFb}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-[#1877F2] hover:bg-[#1664d8] text-white font-medium transition disabled:opacity-50 ml-3 shrink-0"
              >
                <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {connectingFb ? 'Redirecting…' : 'Connect Facebook'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Google Integration */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 dark:border-gray-700 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-sm">Google Sheets</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Connect to write lead data directly to your spreadsheets.
            </p>
          </div>
        </div>

        {user?.googleConnected ? (
          <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm font-medium">
              <HiOutlineCheckCircle size={18} />
              Google account connected
            </div>
            <button
              onClick={handleDisconnectGoogle}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Connect to use Google Sheets nodes in your scenarios.
            </p>
            <button
              onClick={handleConnectGoogle}
              disabled={connecting}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-brand-600 hover:bg-brand-700 text-white font-medium transition disabled:opacity-50"
            >
              Connect Google
            </button>
          </div>
        )}
      </div>

      {/* Webhook Info */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="font-semibold mb-1">Facebook Webhook Configuration</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Add these values in the Facebook Developer Console under&nbsp;
          <strong>Webhooks → leadgen</strong>.
        </p>
        <div className="space-y-3">
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Callback URL pattern</span>
            <code className="block mt-1.5 p-3 bg-gray-50 dark:bg-gray-950 rounded-lg text-xs font-mono break-all border border-gray-200 dark:border-gray-800">
              {(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/webhook/facebook/<span className="text-brand-500">{'<scenarioId>'}</span>
            </code>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Verify Token</span>
            <code className="block mt-1.5 p-3 bg-gray-50 dark:bg-gray-950 rounded-lg text-xs font-mono border border-gray-200 dark:border-gray-800">
              Set <span className="text-amber-600 dark:text-amber-400">FB_VERIFY_TOKEN</span> in your server <span className="text-amber-600 dark:text-amber-400">.env</span>
            </code>
          </div>
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 p-4 text-sm text-blue-900 dark:text-blue-100">
            <p className="font-semibold mb-2">Development testing flow</p>
            <ol className="list-decimal list-inside space-y-1 text-xs text-blue-800 dark:text-blue-200">
              <li>Connect Facebook here and confirm both pages appear in the list above.</li>
              <li>In the scenario builder, choose the page, choose the lead form, then subscribe the page.</li>
              <li>Set your Meta App Dashboard webhook callback to the exact scenario URL you want to test.</li>
              <li>Use the Lead Ads Testing tool to submit a test lead, then check Execution Logs for the new run.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
